/**
 * Stopwatch time helpers — ported verbatim from the web EnterResults logic so
 * web and mobile validate identically.
 */

/**
 * Validates a stopwatch time string in "HH:MM:SS" form where each segment is
 * exactly two digits and the seconds segment is <= 60.
 * (Mirrors EnterResults.validateStopWatchTime.)
 */
export const validateStopWatchTime = (stopWatchTime: string): boolean => {
  const searchTimeSplit = stopWatchTime.split(":");

  if (searchTimeSplit.length === 3) {
    for (const s in searchTimeSplit) {
      if (searchTimeSplit[s].length !== 2) {
        return false;
      }

      const isNumber = parseInt(searchTimeSplit[s], 10);
      if (isNaN(isNumber)) {
        return false;
      }

      if (Number(searchTimeSplit[1]) > 60) {
        return false;
      }
    }

    return true;
  }

  return false;
};

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * Format a number of seconds as the "MM:SS:ms" clock string used across the
 * scoring UI (e.g. 732.12 → "12:12:12"). The milliseconds segment is the raw
 * decimal portion of the input (matching the web Results grid's formatting),
 * so 45.5 → "00:45:5". Returns "" for empty/invalid input. Minutes wrap at 60.
 */
export const formatSecondsToClock = (time?: string | number): string => {
  if (time === undefined || time === null || time === "") return "";
  const n = typeof time === "string" ? Number(time) : time;
  if (isNaN(n)) return "";
  const ms = n.toString().split(".")[1] || "00";
  const whole = Math.max(0, Math.floor(n));
  return `${pad2(Math.floor(whole / 60) % 60)}:${pad2(whole % 60)}:${ms}`;
};

/**
 * Convert a total number of seconds (decimal) into the "MM:SS:TT" stopwatch
 * display string. Ported verbatim from the web StopWatch.convertTimeToMinutes.
 */
export const convertSecondsToStopwatch = (time: number): string => {
  const minutes: number = Math.floor(time / 60);
  const seconds: number = time - minutes * 60;
  const thousands: number = Math.round((seconds - Math.floor(seconds)) * 100);

  const mm =
    minutes.toString().length === 1 ? "0" + minutes : minutes.toString();
  const ss =
    Math.floor(seconds).toString().length === 1
      ? "0" + Math.floor(seconds)
      : Math.floor(seconds).toString();
  const tt =
    thousands.toString().length === 1 ? "0" + thousands : thousands.toString();

  return `${mm}:${ss}:${tt}`;
};

/**
 * Convert a "MM:SS:TT" stopwatch display string into total seconds (decimal).
 * Ported verbatim from the web StopWatch.convertTimeToSeconds.
 */
export const convertStopwatchToSeconds = (time: string): number => {
  const timeSplit: string[] = time.split(":");
  let timeInSeconds = 0;

  if (timeSplit.length === 3) {
    if (Number(timeSplit[0]) > 0) {
      timeInSeconds += Number(timeSplit[0]) * 60;
    }
    timeInSeconds += Number(timeSplit[1]);
  }

  return Number(timeInSeconds + "." + timeSplit[2]);
};

/**
 * Parse "M:SS.TT" or "M:SS:TT" (or "M:SS") into seconds. Returns null when
 * unparseable. (Mirrors barn-hunt bhParseTime.)
 */
export const parseBarnHuntTime = (timeStr?: string): number | null => {
  if (!timeStr) return null;
  const match =
    timeStr.match(/^(\d+)[:](\d+)[.:](\d+)$/) || timeStr.match(/^(\d+)[:](\d+)$/);
  if (!match) return null;
  const mins = parseInt(match[1], 10);
  const secs = parseInt(match[2], 10);
  const hundredths = match[3] ? parseInt(match[3], 10) : 0;
  return mins * 60 + secs + hundredths / 100;
};
